import { Button } from "@/components/ui/button";
import { Share2, Twitter, Facebook, Linkedin, MessageCircle } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

interface SocialShareButtonsProps {
  title: string;
  excerpt: string;
  url: string;
  image?: string;
}

export const SocialShareButtons = ({ title, excerpt, url, image }: SocialShareButtonsProps) => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  // Optimize text for each platform
  const getOptimizedText = (platform: string) => {
    switch (platform) {
      case 'twitter':
        // Twitter: Keep it concise (280 chars), add hashtags
        return `${title}\n\n${excerpt.slice(0, 120)}...\n\n#Travel #TravelTips #OdyVoy`;
      case 'linkedin':
        // LinkedIn: Professional tone, longer is fine
        return `${title}\n\n${excerpt}\n\nRead more:`;
      case 'facebook':
        // Facebook: Engaging, emoji-friendly
        return `✈️ ${title}\n\n${excerpt}\n\nRead the full article:`;
      case 'whatsapp':
        // WhatsApp: Casual, conversational
        return `Hey! Check out this travel article:\n\n*${title}*\n\n${excerpt}\n\n`;
      case 'pinterest':
        // Pinterest: Visual focus
        return `${title} - ${excerpt.slice(0, 100)}...`;
      default:
        return `${title}\n\n${excerpt}`;
    }
  };

  const handleShare = (platform: string) => {
    const encodedUrl = encodeURIComponent(url);
    let shareUrl = '';

    switch (platform) {
      case 'twitter':
        const twitterText = encodeURIComponent(getOptimizedText('twitter'));
        shareUrl = `https://twitter.com/intent/tweet?text=${twitterText}&url=${encodedUrl}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodeURIComponent(getOptimizedText('facebook'))}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'whatsapp':
        const whatsappText = encodeURIComponent(getOptimizedText('whatsapp') + url);
        shareUrl = `https://wa.me/?text=${whatsappText}`;
        break;
      case 'pinterest':
        const pinterestDesc = encodeURIComponent(getOptimizedText('pinterest'));
        const imageUrl = image ? encodeURIComponent(image) : '';
        shareUrl = `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${pinterestDesc}${imageUrl ? `&media=${imageUrl}` : ''}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        toast({
          title: "Link copied!",
          description: "Article link copied to clipboard",
        });
        setIsOpen(false);
        return;
      default:
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400,noopener,noreferrer');
      setIsOpen(false);
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Share2 className="h-4 w-4" />
          Share
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => handleShare('twitter')} className="cursor-pointer">
          <Twitter className="h-4 w-4 mr-2" />
          Share on Twitter
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('facebook')} className="cursor-pointer">
          <Facebook className="h-4 w-4 mr-2" />
          Share on Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('linkedin')} className="cursor-pointer">
          <Linkedin className="h-4 w-4 mr-2" />
          Share on LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('whatsapp')} className="cursor-pointer">
          <MessageCircle className="h-4 w-4 mr-2" />
          Share on WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('pinterest')} className="cursor-pointer">
          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
          </svg>
          Share on Pinterest
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('copy')} className="cursor-pointer">
          <Share2 className="h-4 w-4 mr-2" />
          Copy Link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
